import _ from 'lodash';
import { formatToISODate } from '../../../common/utils/dateTimeUtils';

const getPercentage = (value, total) => {
  return total > 0 ? Number(((value / total) * 100).toFixed(2)) : 0;
};

const filterListBasedOnSelectedList = (dataList, selectedList, fieldName) => {
  // Function to filter the dataList based on the selectedList
  // If the dataList item[fieldName] value is present in the selectedList, then the item is included in the filtered list

  if (selectedList.length === 0) {
    return dataList;
  }
  const selectedSet = new Set(selectedList.map((selectedItem) => selectedItem.value));
  return dataList.filter((dataItem) => selectedSet.has(dataItem[fieldName]));
};

const getUniqueOptionsByKey = (records, key) => {
  if (!records?.length || !key) {
    return [];
  }
  const uniqueOptions = new Set();
  records.forEach((item) => {
    if (item?.[key] != null) {
      uniqueOptions.add(item[key]);
    }
  });
  return Array.from(uniqueOptions).map((value) => ({ label: value, value }));
};

const transformUsageDetails = (usageDetails) => {
  return usageDetails.map((item) => ({
    date: item.planDate,
    division: item.division,
    region: item.region,
    terminalCode: item.terminalCode,
    scenarioId: item.scenarioId,
    routesCount: item.totalNumberOfRoutes,
    stopsCount: item.totalNumberOfStops,
    shipmentsCount: item.totalNumberOfShipments,
    cityRoutesCount: item.numberOfCityRoutes,
    trapRoutesCount: item.numberOfTrapRoutes,
    dynamicOptimizerRunsCount: item.numberOfDynamicRoutingRuns,
    sequenceOptimizerRunsCount: item.numberOfSequenceOptimizerRuns,
    manuallyCreatedRoutesCount: item.numberOfRoutesCreatedManually,
    optimizationCreatedRoutesCount: item.numberOfRoutesCreatedByOptimization,
    manuallyAdjustedRoutesCount: item.numberOfRoutesAdjustedManually,
    systemCreatedRoutesCount: item.numberOfRoutesCreatedBySystem,
    totalPublishesCount: item.numberOfPublish,
    planCreationTime: item.planCreationDateTime,
    firstOptimizationTime: item.firstOptimizationDateTime,
    plannersCount: item.numberOfPlanners,
    planners: item.planners
  }));
};

// ------------------------------------
// 1. usageSummary - Groups by terminal and aggregates across all dates
// ------------------------------------
const getUsageSummary = (usageDetails) =>
  _(usageDetails)
    .groupBy('terminalCode')
    .map((entries, terminalCode) => {
      const totalRoutes = _.sumBy(entries, 'routesCount');
      const manuallyCreated = _.sumBy(entries, 'manuallyCreatedRoutesCount');
      const manuallyAdjusted = _.sumBy(entries, 'manuallyAdjustedRoutesCount');
      const optimizationCreated = _.sumBy(entries, 'optimizationCreatedRoutesCount');
      const systemCreated = _.sumBy(entries, 'systemCreatedRoutesCount');
      const cityRoutes = _.sumBy(entries, 'cityRoutesCount');
      const trapRoutes = _.sumBy(entries, 'trapRoutesCount');
      return {
        division: entries[0].division,
        region: entries[0].region,
        terminal: terminalCode,
        totalRoutes,
        cityRoutes: {
          count: cityRoutes,
          percentage: getPercentage(cityRoutes, totalRoutes)
        },
        trapRoutes: {
          count: trapRoutes,
          percentage: getPercentage(trapRoutes, totalRoutes)
        },
        manuallyCreatedRoutes: {
          count: manuallyCreated,
          percentage: getPercentage(manuallyCreated, totalRoutes)
        },
        manuallyAdjustedRoutes: {
          count: manuallyAdjusted,
          percentage: getPercentage(manuallyAdjusted, totalRoutes)
        },
        optimizationCreatedRoutes: {
          count: optimizationCreated,
          percentage: getPercentage(optimizationCreated, totalRoutes)
        },
        systemCreatedRoutes: {
          count: systemCreated,
          percentage: getPercentage(systemCreated, totalRoutes)
        }
      };
    })
    .value();

// ------------------------------------
// 2. usageGraphData - Groups by date and creates nested terminal data
// ------------------------------------
const createRouteMetrics = (count, total) => ({
  count,
  percentage: getPercentage(count, total)
});

const getUsageGraphData = (usageDetails) =>
  _(usageDetails)
    .groupBy('date')
    .map((entries, date) => {
      // Calculate totals for this date
      const totalRoutes = _.sumBy(entries, 'routesCount');
      const manuallyCreated = _.sumBy(entries, 'manuallyCreatedRoutesCount');
      const manuallyAdjusted = _.sumBy(entries, 'manuallyAdjustedRoutesCount');

      // Create terminal-specific data
      const selectedViewData = _(entries)
        .keyBy('terminalCode')
        .mapValues((entry) => ({
          manuallyCreatedRoutes: createRouteMetrics(
            entry.manuallyCreatedRoutesCount,
            entry.routesCount
          ),
          manuallyAdjustedRoutes: createRouteMetrics(
            entry.manuallyAdjustedRoutesCount,
            entry.routesCount
          ),
          totalRoutes: entry.routesCount
        }))
        .value();

      // Create network-level aggregated data
      const networkViewData = {
        manuallyCreatedRoutes: createRouteMetrics(manuallyCreated, totalRoutes),
        manuallyAdjustedRoutes: createRouteMetrics(manuallyAdjusted, totalRoutes),
        totalRoutes
      };

      return {
        date,
        selectedViewData,
        networkViewData
      };
    })
    .value();

// ------------------------------------
// 3. Main function to transform usageDetails into required structure
// ------------------------------------
const transformUsageData = (usageDetails) => {
  const transformedUsageDetails = transformUsageDetails(usageDetails);
  return {
    usageDetails: transformedUsageDetails,
    usageSummary: getUsageSummary(transformedUsageDetails),
    usageGraphData: getUsageGraphData(transformedUsageDetails)
  };
};

const getPayloadForUsageReport = (filters) => {
  const { terminals, dateRange } = filters || {};

  const terminalCodes = terminals.map((option) => option.value);

  const payload = {
    terminalCodes,
    startDate: formatToISODate(dateRange.startDate),
    endDate: formatToISODate(dateRange.endDate)
  };

  return payload;
};

export {
  transformUsageData,
  getPercentage,
  getPayloadForUsageReport,
  filterListBasedOnSelectedList,
  getUniqueOptionsByKey
};
